/**
 * useTaskChecklist - Hook para checklist da tarefa
 *
 * Gerencia grupos, itens e comentários com @menção via RPCs SECURITY DEFINER.
 * Fornece query agregada + mutations para todas as ações do bloco.
 *
 * @version 2.0.0
 * RELEASE: 2026-07-07 v1.0.0 - Criação inicial (grupos + itens)
 * RELEASE: 2026-07-07 v2.0.0 - Fase 2: comentário por item + @menções
 */
import { useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { createLogger } from '@/lib/logger';

// Logger dedicado
const logger = createLogger('useTaskChecklist');

/** Menção em comentário de item */
export interface ChecklistItemMention {
  id: string;
  mentioned_user_id: string;
  mentioned_user_name: string | null;
  read_at: string | null;
}

/** Anexo vinculado a um comentário de item */
export interface ChecklistItemCommentAttachment {
  id: string;
  file_name: string;
  file_type: string;
  file_size: number | null;
  public_url: string;
  thumbnail_url: string | null;
  mime_type: string | null;
  storage_path: string | null;
}

/** Comentário de um item (thread — N por item) */
export interface ChecklistItemComment {
  id: string;
  content: string;
  author_id: string;
  author_name: string | null;
  author_avatar: string | null;
  created_at: string;
  updated_at: string;
  mentions: ChecklistItemMention[];
  attachments: ChecklistItemCommentAttachment[];
}

/** Item marcável do checklist */
export interface ChecklistItem {
  id: string;
  content: string;
  is_done: boolean;
  done_at: string | null;
  done_by: string | null;
  done_by_name: string | null;
  done_by_avatar: string | null;
  sort_order: number;
  created_at: string;
  /** Thread de comentários (v2.0.0 — antes era um único `comment`) */
  comments: ChecklistItemComment[];
}

/** Grupo de checklist da tarefa */
export interface ChecklistGroup {
  id: string;
  title: string;
  sort_order: number;
  created_at: string;
  items: ChecklistItem[];
}


/**
 * Hook principal do checklist da tarefa
 * Query agregada + mutations com invalidação centralizada.
 */
export function useTaskChecklist(taskId: string | null, tenantId?: string | null) {
  const { currentTenant } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const effectiveTenant = tenantId ?? currentTenant?.id ?? null;

  // Query agregada - carrega grupos + itens em única chamada
  const query = useQuery({
    queryKey: ['task-checklist', taskId],
    queryFn: async () => {
      logger.debug('Carregando checklist', { taskId });
      const { data, error } = await supabase.rpc('get_task_checklist', {
        p_task_id: taskId!,
        p_tenant_hint: effectiveTenant,
      });
      if (error) throw error;
      const parsed = typeof data === 'string' ? JSON.parse(data) : data;
      return (parsed ?? []) as ChecklistGroup[];
    },
    enabled: !!taskId,
    staleTime: 15_000,
  });

  // Invalidação padrão após qualquer mutation
  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['task-checklist', taskId] });
    queryClient.invalidateQueries({ queryKey: ['task-activity-log', taskId] });
  }, [queryClient, taskId]);

  // Handler genérico de erro para toasts
  const showError = useCallback((title: string, err: unknown) => {
    logger.error(title, { error: (err as Error)?.message });
    toast({
      title,
      description: (err as Error)?.message ?? 'Erro desconhecido',
      variant: 'destructive',
    });
  }, [toast]);

  // ---------- Mutations de grupo ----------
  const createGroup = useMutation({
    mutationFn: async (title: string) => {
      const { data, error } = await supabase.rpc('create_task_checklist_group', {
        p_task_id: taskId!,
        p_title: title,
        p_tenant_hint: effectiveTenant,
      });
      if (error) throw error;
      return data as string;
    },
    onSuccess: invalidate,
    onError: (err) => showError('Erro ao criar lista', err),
  });

  const updateGroup = useMutation({
    mutationFn: async (params: { groupId: string; title: string }) => {
      const { error } = await supabase.rpc('update_task_checklist_group', {
        p_group_id: params.groupId,
        p_title: params.title,
        p_tenant_hint: effectiveTenant,
      });
      if (error) throw error;
    },
    onSuccess: invalidate,
    onError: (err) => showError('Erro ao renomear lista', err),
  });

  const deleteGroup = useMutation({
    mutationFn: async (groupId: string) => {
      const { error } = await supabase.rpc('delete_task_checklist_group', {
        p_group_id: groupId,
        p_tenant_hint: effectiveTenant,
      });
      if (error) throw error;
    },
    onSuccess: invalidate,
    onError: (err) => showError('Erro ao excluir lista', err),
  });

  const bulkToggleGroup = useMutation({
    mutationFn: async (params: { groupId: string; isDone: boolean }) => {
      const { error } = await supabase.rpc('bulk_toggle_task_checklist_group', {
        p_group_id: params.groupId,
        p_is_done: params.isDone,
        p_tenant_hint: effectiveTenant,
      });
      if (error) throw error;
    },
    onSuccess: invalidate,
    onError: (err) => showError('Erro ao atualizar lista', err),
  });

  const reorderGroups = useMutation({
    mutationFn: async (groupIds: string[]) => {
      const { error } = await supabase.rpc('reorder_task_checklist_groups', {
        p_task_id: taskId!,
        p_group_ids: groupIds,
        p_tenant_hint: effectiveTenant,
      });
      if (error) throw error;
    },
    onSuccess: invalidate,
    onError: (err) => showError('Erro ao reordenar listas', err),
  });

  // ---------- Mutations de item ----------
  const createItem = useMutation({
    mutationFn: async (params: { groupId: string; content: string }) => {
      const { data, error } = await supabase.rpc('create_task_checklist_item', {
        p_group_id: params.groupId,
        p_content: params.content,
        p_tenant_hint: effectiveTenant,
      });
      if (error) throw error;
      return data as string;
    },
    onSuccess: invalidate,
    onError: (err) => showError('Erro ao adicionar item', err),
  });

  const updateItem = useMutation({
    mutationFn: async (params: { itemId: string; content: string }) => {
      const { error } = await supabase.rpc('update_task_checklist_item', {
        p_item_id: params.itemId,
        p_content: params.content,
        p_tenant_hint: effectiveTenant,
      });
      if (error) throw error;
    },
    onSuccess: invalidate,
    onError: (err) => showError('Erro ao editar item', err),
  });

  const toggleItem = useMutation({
    mutationFn: async (params: { itemId: string; isDone: boolean }) => {
      const { error } = await supabase.rpc('toggle_task_checklist_item', {
        p_item_id: params.itemId,
        p_is_done: params.isDone,
        p_tenant_hint: effectiveTenant,
      });
      if (error) throw error;
    },
    onSuccess: invalidate,
    onError: (err) => showError('Erro ao marcar item', err),
  });

  const deleteItem = useMutation({
    mutationFn: async (itemId: string) => {
      const { error } = await supabase.rpc('delete_task_checklist_item', {
        p_item_id: itemId,
        p_tenant_hint: effectiveTenant,
      });
      if (error) throw error;
    },
    onSuccess: invalidate,
    onError: (err) => showError('Erro ao excluir item', err),
  });

  // ---------- Mutations de comentário do item (Fase 3 — thread) ----------
  /**
   * upsertItemComment
   * - commentId ausente/null => cria um novo comentário
   * - commentId informado    => atualiza comentário existente (autor/admin)
   * attachmentIds vincula anexos recém-upados; removedAttachmentIds apaga anexos
   * marcados na edição.
   */
  const upsertItemComment = useMutation({
    mutationFn: async (params: {
      itemId: string;
      commentId?: string | null;
      content: string;
      mentionedUserIds: string[];
      attachmentIds?: string[];
      removedAttachmentIds?: string[];
    }) => {
      const { data, error } = await supabase.rpc('upsert_task_checklist_item_comment', {
        p_item_id: params.itemId,
        p_content: params.content,
        p_mentioned_user_ids: params.mentionedUserIds,
        p_comment_id: params.commentId ?? null,
        p_attachment_ids: params.attachmentIds ?? [],
        p_removed_attachment_ids: params.removedAttachmentIds ?? [],
        p_tenant_hint: effectiveTenant,
      });
      if (error) throw error;
      return data as string;
    },
    onSuccess: invalidate,
    onError: (err) => showError('Erro ao salvar comentário', err),
  });

  const deleteItemComment = useMutation({
    mutationFn: async (commentId: string) => {
      const { error } = await supabase.rpc('delete_task_checklist_item_comment', {
        p_comment_id: commentId,
        p_tenant_hint: effectiveTenant,
      });
      if (error) throw error;
    },
    onSuccess: invalidate,
    onError: (err) => showError('Erro ao excluir comentário', err),
  });

  const markMentionRead = useMutation({
    mutationFn: async (mentionId: string) => {
      const { error } = await supabase.rpc('mark_task_checklist_mention_read', {
        p_mention_id: mentionId,
        p_tenant_hint: effectiveTenant,
      });
      if (error) throw error;
    },
    onSuccess: invalidate,
  });
  const reorderItems = useMutation({
    mutationFn: async (params: { groupId: string; itemIds: string[] }) => {
      const { error } = await supabase.rpc('reorder_task_checklist_items', {
        p_group_id: params.groupId,
        p_item_ids: params.itemIds,
        p_tenant_hint: effectiveTenant,
      });
      if (error) throw error;
    },
    onSuccess: invalidate,
    onError: (err) => showError('Erro ao reordenar itens', err),
  });


  return {
    groups: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
    // Grupos
    createGroup: createGroup.mutateAsync,
    updateGroup: updateGroup.mutateAsync,
    deleteGroup: deleteGroup.mutateAsync,
    bulkToggleGroup: bulkToggleGroup.mutateAsync,
    reorderGroups: reorderGroups.mutateAsync,
    // Itens
    createItem: createItem.mutateAsync,
    updateItem: updateItem.mutateAsync,
    toggleItem: toggleItem.mutateAsync,
    deleteItem: deleteItem.mutateAsync,
    reorderItems: reorderItems.mutateAsync,
    // Comentários (Fase 2)
    upsertItemComment: upsertItemComment.mutateAsync,
    deleteItemComment: deleteItemComment.mutateAsync,
    markMentionRead: markMentionRead.mutateAsync,
    // Estados
    isMutating:
      createGroup.isPending || updateGroup.isPending || deleteGroup.isPending ||
      bulkToggleGroup.isPending || reorderGroups.isPending ||
      createItem.isPending || updateItem.isPending || toggleItem.isPending ||
      deleteItem.isPending || reorderItems.isPending ||
      upsertItemComment.isPending || deleteItemComment.isPending,
  };
}

